import React from 'react';
import { notificationTypes } from 'new/components/monitor/ResourceTriage/consts';
import { Stats } from 'new/elements/Stats';
import ResourceActivity from 'new/elements/support/detailTabs/ResourceActivity';
import ResourceErrorData from 'new/elements/support/detailTabs/ResourceErrorData';
import { ResourceNotifications } from 'new/elements/support/detailTabs/ResourceNotifications';
import { ResourceReadStats } from 'new/elements/support/detailTabs/ResourceReadStats';
import { NEXSET_TAB_IDS } from 'new/utils/nexsetTabs';
import AvailableDataSetCardOverviewTabContainer from 'js/components/data_sets_available/cards/AvailableDataSetCardOverviewTabContainer';
import AvailableDataSetDataBrowserContainer from 'js/components/data_sets_available/cards/AvailableDataSetDataBrowserContainer';
import DataSetCardOverviewTabContainer from 'js/components/data_sets/cards/DataSetCardOverviewTabContainer';
import DataSetDataBrowserContainer from 'js/components/data_sets/cards/DataSetDataBrowserContainer';
import DataSetRecipeTab from 'js/components/data_sets/cards/DataSetRecipeTab';
import DataSinkOverviewTabContainer from 'js/components/data_sinks/cards/DataSinkOverviewTabContainer';
import DataSourceOverviewTabContainer from 'js/components/data_sources/cards/DataSourceOverviewTabContainer';
import FileUploadTab from 'js/components/data_sources/FileUploadTab';
import AclTabViewContainer from 'js/components/elements/aclView/AclTabViewContainer';
import { ACL_RESOURCE_TYPE_DF_DATASET } from 'js/constants/aclConstants';
import {
  CONNECTION_CATEGORY_FILE_UPLOAD,
  CONNECTION_CATEGORY_SYSLOG,
} from 'js/constants/connectionCategories';
import { WHITELABEL_SETTINGS } from 'js/constants/config';
import {
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
} from 'js/constants/resourceTypes';
import { isSqlAutomation } from 'js/lib/ResourceUtils';

const isThemeOAI = WHITELABEL_SETTINGS.pagingMode === 'oai';

export const getSourceTabs = (
  data: Record<string, any>,
  getMetaInfoById: (id: string | undefined) => Record<string, any> | undefined,
  vendors: Record<string, any> = {},
) => {
  const sourceTabsMap = {
    overviewTab: {
      tabName: 'Overview',
      tabElement: <DataSourceOverviewTabContainer />,
    },
    readStatsTab: {
      tabName: 'Read Stats',
      tabElement: <ResourceReadStats resourceType={RESOURCE_TYPE_SOURCE} />,
    },
    notificationsTab: {
      tabName: 'Notifications',
      tabElement: (
        <ResourceNotifications notificationType={notificationTypes[RESOURCE_TYPE_SOURCE]} />
      ),
    },
    activityTab: {
      tabName: 'Activity',
      tabElement: <ResourceActivity resourceType={RESOURCE_TYPE_SOURCE} />,
    },
    errorDataTab: {
      tabName: 'Error Data',
      tabElement: <ResourceErrorData resourceType={RESOURCE_TYPE_SOURCE} />,
    },
  };

  if (isSqlAutomation(data, vendors)) {
    return [sourceTabsMap.overviewTab, sourceTabsMap.notificationsTab, sourceTabsMap.activityTab];
  }

  const sourceTabs = [sourceTabsMap.overviewTab];

  const commonSourceTabs = [
    sourceTabsMap.readStatsTab,
    sourceTabsMap.notificationsTab,
    sourceTabsMap.activityTab,
    sourceTabsMap.errorDataTab,
  ];

  const selectedSourceType = getMetaInfoById(data?.source_type);
  if (selectedSourceType) {
    const isFileUploadSourceType =
      selectedSourceType.connectionCategory === CONNECTION_CATEGORY_FILE_UPLOAD;

    const isSyslogSourceType = selectedSourceType.connectionCategory === CONNECTION_CATEGORY_SYSLOG;

    const isApiServer = data?.flow_type === 'api_server';

    if (isSyslogSourceType || isApiServer) {
      sourceTabs.push(...[sourceTabsMap.notificationsTab, sourceTabsMap.activityTab]);
    } else {
      sourceTabs.push(...commonSourceTabs);
    }

    if (isFileUploadSourceType) {
      sourceTabs.push({
        tabName: 'File Upload',
        tabElement: <FileUploadTab sourceID={data.id} />,
      });
    }
  } else {
    sourceTabs.push(...commonSourceTabs);
  }

  if (data.node_type === 'query_prompt' || data.flow_type === 'rag') {
    return [
      {
        tabName: 'Overview',
        tabElement: <DataSourceOverviewTabContainer />,
      },
      {
        tabName: 'Activity',
        tabElement: <ResourceActivity resourceType={RESOURCE_TYPE_SOURCE} />,
      },
    ];
  }

  return sourceTabs;
};

export const dataSinkTabs = (data: Record<string, any>) => {
  if (data.node_type === 'llm_processor' || data.flow_type === 'rag') {
    return [
      {
        tabName: 'Overview',
        tabElement: <DataSinkOverviewTabContainer />,
      },
      {
        tabName: 'Activity',
        tabElement: <ResourceActivity resourceType={RESOURCE_TYPE_SINK} />,
      },
    ];
  }
  return [
    {
      tabName: 'Overview',
      tabElement: <DataSinkOverviewTabContainer />,
    },
    {
      tabName: 'Write Stats',
      tabElement: <ResourceReadStats resourceType={RESOURCE_TYPE_SINK} />,
    },
    {
      tabName: 'Notifications',
      tabElement: (
        <ResourceNotifications notificationType={notificationTypes[RESOURCE_TYPE_SINK]} />
      ),
    },
    {
      tabName: 'Activity',
      tabElement: <ResourceActivity resourceType={RESOURCE_TYPE_SINK} />,
    },
    {
      tabName: 'Error Data',
      tabElement: <ResourceErrorData resourceType={RESOURCE_TYPE_SINK} />,
    },
  ];
};

export const getNexsetTabComponent = (tabId: string, data: any) => {
  switch (tabId) {
    case NEXSET_TAB_IDS.OVERVIEW:
      if (data?.dataSetType === 'shared') {
        return <AvailableDataSetCardOverviewTabContainer />;
      }
      return <DataSetCardOverviewTabContainer isThemeOAI={isThemeOAI} />;

    case NEXSET_TAB_IDS.SCHEMA:
      if (data?.dataSetType === 'shared') {
        return <AvailableDataSetDataBrowserContainer />;
      }
      return <DataSetDataBrowserContainer data={data} />;

    case NEXSET_TAB_IDS.RULES:
      return <DataSetRecipeTab />;

    case NEXSET_TAB_IDS.STATS:
      return <Stats resourceType={RESOURCE_TYPE_DATA_SET} />;

    case NEXSET_TAB_IDS.NOTIFICATIONS:
      return <ResourceNotifications notificationType={notificationTypes[RESOURCE_TYPE_DATA_SET]} />;

    case NEXSET_TAB_IDS.ACTIVITY:
      return <ResourceActivity resourceType={RESOURCE_TYPE_DATA_SET} />;

    case NEXSET_TAB_IDS.ERROR_DATA:
      return <ResourceErrorData resourceType={RESOURCE_TYPE_DATA_SET} />;

    case NEXSET_TAB_IDS.SHARE:
      return <AclTabViewContainer resourceType={ACL_RESOURCE_TYPE_DF_DATASET} />;

    default:
      return null;
  }
};
